import changelog from '@/app-changelog.js';
import PublicPagination from '@/components/public/PublicPagination/PublicPagination.vue';
import {
  CHANGELOG_READ_STORAGE_KEY,
  canShowChangelogItemForRoles,
  getChangelogEntryKey,
  getChangelogItemKey,
  getChangelogRoleKeys,
  isChangelogEntryUnreadEligible,
  loadChangelogReadItemKeys,
  normalizeChangelogRole,
} from '@/services/ChangelogService';

export default {
  name: 'ChangelogFeed',
  components: { PublicPagination },
  props: {
    history: { type: Boolean, default: false },
    showEmpty: { type: Boolean, default: true },
  },

  emits: ['unread-change', 'open-history'],

  data() {
    return {
      page: 1,
      pageSize: 10,
      expandedEntryKeys: [],
      readItemKeys: this.loadReadItemKeys(),
    };
  },

  computed: {
    requestedRelease() { return this.history ? this.$route?.query?.release : undefined; },

    /**
     * Возвращает роли текущего пользователя в формате changelog roles.
     * @returns {string[]}
     */
    currentRoleKeys() {
      return getChangelogRoleKeys(this.$user);
    },

    /**
     * Возвращает changelog entries с отфильтрованными по ролям пунктами.
     * @returns {Array}
     */
    visibleEntries() {
      return (Array.isArray(changelog) ? changelog : [])
        .map((entry, entryIndex) => {
          const entryKey = this.entryKey(entry, entryIndex);
          const items = this.normalizeItems(entry?.items)
            .map((item, itemIndex) => {
              const itemKey = this.itemKey(entry, entryIndex, itemIndex);
              return {
                ...item,
                key: itemKey,
                unread: !this.history && isChangelogEntryUnreadEligible(entry) && !this.readItemKeys.includes(itemKey),
              };
            })
            .filter((item) => this.canShowForRoles(item.roles));

          const expanded = this.isExpanded(entryKey);
          const fullText = this.history ? this.localizedChangelogValue(entry, 'fullText').trim() : '';
          return {
            key: entryKey,
            version: entry?.version || '',
            versionLabel: this.getVersionLabel(entry?.version, entry?.build, entry?.commit),
            date: entry?.date || '',
            title: this.localizedChangelogValue(entry, 'title') || this.$t('notifications.changes'),
            items,
            hasFullText: Boolean(this.localizedChangelogValue(entry, 'fullText').trim()) || items.some((item) => item.hasFullText),
            displayItems: fullText && expanded ? [{
              key: `${entryKey}:full`,
              descriptionTokens: this.parseDescription(fullText),
              fullEntry: true,
            }] : items,
            canCollapse: this.history ? Boolean(fullText) || items.some((item) => item.hasFullText) : items.length > 0,
            isCollapsed: !expanded,
            hasUnread: items.some((item) => item.unread),
          };
        })
        .filter((entry) => entry.items.length > 0 && (this.history || isChangelogEntryUnreadEligible(entry)))
        .sort((a, b) => this.compareEntriesDesc(a, b));
    },

    /**
     * Есть ли непрочитанные пункты, доступные текущему пользователю.
     * @returns {boolean}
     */
    totalPages() { return Math.max(1, Math.ceil(this.visibleEntries.length / this.pageSize)); },
    displayedEntries() {
      return this.history ? this.visibleEntries.slice((this.page - 1) * this.pageSize, this.page * this.pageSize) : this.visibleEntries;
    },
    hasUnread() {
      return this.visibleEntries.some((entry) => entry.hasUnread);
    },
  },

  watch: {
    requestedRelease() { this.openRequestedRelease(); },
    currentRoleKeys() { this.openRequestedRelease(); },
    pageSize() { this.page = 1; },
    totalPages(value) { this.page = Math.min(this.page, value); },
    hasUnread: {
      immediate: true,
      handler(value) {
        this.$emit('unread-change', value);
      },
    },
  },

  mounted() { this.openRequestedRelease(); },

  methods: {
    async openRequestedRelease() {
      const key = this.requestedRelease;
      if (typeof key !== 'string') return;
      const index = this.visibleEntries.findIndex((entry) => entry.key === key);
      if (index < 0) return;
      this.page = Math.floor(index / this.pageSize) + 1;
      if (!this.isExpanded(key)) this.expandedEntryKeys.push(key);
      await this.$nextTick();
      const entry = Array.from(this.$el.querySelectorAll('[data-notification-entry-key]'))
        .find((element) => element.dataset.notificationEntryKey === key);
      entry?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    /**
     * Нормализует роль к формату changelog.
     * @param {string} value
     * @returns {string}
     */
    normalizeRole(value) {
      return normalizeChangelogRole(value);
    },

    /**
     * Проверяет, доступен ли пункт текущим ролям.
     * @param {string[]|undefined} roles
     * @returns {boolean}
     */
    canShowForRoles(roles) {
      return canShowChangelogItemForRoles(roles, this.currentRoleKeys);
    },

    localizedChangelogValue(source, field) {
      if (!source) return '';
      return this.$i18n.locale === 'en'
        ? source[`${field}En`] || source[field] || ''
        : source[field] || '';
    },

    /**
     * Приводит пункты changelog к объектам.
     * @param {Array} items
     * @returns {{text:string,description:string,roles:string[]}[]}
     */
    normalizeItems(items) {
      return (Array.isArray(items) ? items : [])
        .map((item) => {
          if (typeof item === 'string') {
            return { text: item, description: '', descriptionTokens: [], roles: [] };
          }
          const fullText = this.normalizeTextBlock(this.localizedChangelogValue(item, 'fullText'));
          const description = this.normalizeTextBlock(
            (this.history && fullText) || this.localizedChangelogValue(item, 'description'),
          );
          return {
            hasFullText: Boolean(fullText),
            text: this.localizedChangelogValue(item, 'text'),
            description,
            descriptionTokens: this.parseDescription(description),
            roles: item?.roles || [],
          };
        })
        .filter((item) => item.text);
    },

    /**
     * Нормализует многострочный текст из changelog.
     * @param {string} value
     * @returns {string}
     */
    normalizeTextBlock(value) {
      return String(value)
        .replace(/\r\n/g, '\n')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .join('\n');
    },

    /**
     * Разбирает безопасное подмножество markdown в токены для Vue render.
     * @param {string} value
     * @returns {{type:string,text?:string,href?:string,color?:string,iconClass?:string,iconStyle?:Object}[]}
     */
    parseDescription(value) {
      const source = String(value || '');
      const tokens = [];
      const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\{(?:success|warning|danger|icon):[^}]+\}|\n)/g;
      let lastIndex = 0;
      let match;

      while ((match = pattern.exec(source)) !== null) {
        if (match.index > lastIndex) {
          tokens.push({ type: 'text', text: source.slice(lastIndex, match.index) });
        }

        tokens.push(this.parseDescriptionToken(match[0]));
        lastIndex = pattern.lastIndex;
      }

      if (lastIndex < source.length) {
        tokens.push({ type: 'text', text: source.slice(lastIndex) });
      }

      return tokens.filter((token) => token.type !== 'text' || token.text);
    },

    /**
     * Разбирает один markdown-токен.
     * @param {string} token
     * @returns {{type:string,text?:string,href?:string,color?:string,iconClass?:string,iconStyle?:Object}}
     */
    parseDescriptionToken(token) {
      if (token === '\n') return { type: 'br' };
      if (token.startsWith('**') && token.endsWith('**')) {
        return { type: 'strong', text: token.slice(2, -2) };
      }
      if (token.startsWith('*') && token.endsWith('*')) {
        return { type: 'em', text: token.slice(1, -1) };
      }
      if (token.startsWith('`') && token.endsWith('`')) {
        return { type: 'code', text: token.slice(1, -1) };
      }

      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      if (linkMatch) {
        const href = this.normalizeLinkHref(linkMatch[2]);
        if (href) return { type: 'link', text: linkMatch[1], href };
      }

      const colorMatch = token.match(/^\{(success|warning|danger):([^}]+)\}$/);
      if (colorMatch) {
        return { type: 'color', color: colorMatch[1], text: colorMatch[2] };
      }

      const iconMatch = token.match(/^\{icon:([^}]+)\}$/);
      if (iconMatch) {
        const [iconClass, iconColor] = iconMatch[1].split('|').map((part) => part.trim());
        return {
          type: 'icon',
          iconClass: this.normalizeIconClass(iconClass),
          iconStyle: this.normalizeIconStyle(iconColor),
        };
      }

      return { type: 'text', text: token };
    },

    /**
     * Оставляет внешние HTTP(S)-ссылки без изменений, а ссылки без протокола
     * привязывает к текущему домену приложения.
     * @param {string} value
     * @returns {string}
     */
    normalizeLinkHref(value) {
      const href = String(value || '').trim();
      if (/^https?:\/\//i.test(href)) return href;
      if (!href || /^[a-z][a-z\d+.-]*:/i.test(href)) return '';

      const path = `/${href.replace(/^\/+/, '')}`;
      return typeof window === 'undefined' ? path : `${window.location.origin}${path}`;
    },

    /**
     * Нормализует класс Bootstrap Icons: позволяет писать только bi-name.
     * @param {string} value
     * @returns {string}
     */
    normalizeIconClass(value) {
      const classNames = String(value || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      if (!classNames.some((className) => className === 'bi')) {
        classNames.unshift('bi');
      }

      return classNames.join(' ');
    },

    /**
     * Нормализует цвет иконки к безопасному inline style.
     * @param {string} value
     * @returns {Object}
     */
    normalizeIconStyle(value) {
      const namedColors = {
        success: 'var(--bs-success)',
        warning: 'var(--app-accent-gold)',
        danger: 'var(--bs-danger-text-emphasis)',
        primary: 'var(--app-accent-gold)',
        secondary: 'var(--app-text-muted)',
        muted: 'var(--app-text-muted)',
      };
      const color = String(value || '').trim();
      if (!color) return {};
      if (namedColors[color]) return { color: namedColors[color] };
      if (/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(color)) {
        return { color };
      }
      if (/^rgba?\(\s*(?:\d{1,3}\s*,\s*){2}\d{1,3}(?:\s*,\s*(?:0|1|0?\.\d+))?\s*\)$/.test(color)) {
        return { color };
      }
      return {};
    },

    /**
     * Возвращает отображаемую версию блока changelog.
     * @param {string} version
     * @param {string|number} build
     * @param {string} commit
     * @returns {string}
     */
    getVersionLabel(version, build, commit) {
      if (!version) return '';
      const buildNumber = String(build || '').trim();
      const commitHash = String(commit || '').trim();
      const versionWithBuild = buildNumber && buildNumber !== 'AUTO'
        ? `${version}.${buildNumber}`
        : version;
      return commitHash && commitHash !== 'AUTO'
        ? `${versionWithBuild} (${commitHash})`
        : versionWithBuild;
    },

    /**
     * Сравнивает блоки changelog: новые даты и версии выше.
     * @param {Object} a
     * @param {Object} b
     * @returns {number}
     */
    compareEntriesDesc(a, b) {
      const dateA = Date.parse(a?.date || '') || 0;
      const dateB = Date.parse(b?.date || '') || 0;
      if (dateA !== dateB) return dateB - dateA;
      return this.compareVersionsDesc(a?.version || '', b?.version || '');
    },

    /**
     * Сравнивает semver-like версии по убыванию.
     * @param {string} a
     * @param {string} b
     * @returns {number}
     */
    compareVersionsDesc(a, b) {
      const partsA = String(a).match(/\d+/g)?.map(Number) || [];
      const partsB = String(b).match(/\d+/g)?.map(Number) || [];
      const length = Math.max(partsA.length, partsB.length);
      for (let i = 0; i < length; i += 1) {
        const valueA = partsA[i] || 0;
        const valueB = partsB[i] || 0;
        if (valueA !== valueB) return valueB - valueA;
      }
      return 0;
    },

    /**
     * Возвращает ключ блока changelog.
     * @param {Object} entry
     * @param {number} entryIndex
     * @returns {string}
     */
    entryKey(entry, entryIndex) {
      return getChangelogEntryKey(entry, entryIndex);
    },

    /**
     * Возвращает стабильный ключ пункта changelog.
     * @param {Object} entry
     * @param {number} entryIndex
     * @param {number} itemIndex
     * @returns {string}
     */
    itemKey(entry, entryIndex, itemIndex) {
      return getChangelogItemKey(entry, entryIndex, itemIndex);
    },

    /**
     * Загружает прочитанные пункты changelog из localStorage.
     * @returns {string[]}
     */
    loadReadItemKeys() {
      return loadChangelogReadItemKeys();
    },

    /**
     * Сохраняет прочитанные пункты changelog в localStorage.
     */
    saveReadItemKeys() {
      localStorage.setItem(CHANGELOG_READ_STORAGE_KEY, JSON.stringify(this.readItemKeys));
    },

    /**
     * Помечает все видимые текущему пользователю пункты как прочитанные.
     */
    markVisibleAsRead() {
      const keys = this.visibleEntries.flatMap((entry) => entry.items.map((item) => item.key));
      this.readItemKeys = Array.from(new Set([...this.readItemKeys, ...keys]));
      this.saveReadItemKeys();
      this.$emit('unread-change', this.hasUnread);
    },

    /**
     * Проверяет, развернут ли блок changelog.
     * @param {string} key
     * @returns {boolean}
     */
    isExpanded(key) {
      return this.expandedEntryKeys.includes(key);
    },

    /**
     * Переключает развернутость блока changelog.
     * @param {string} key
     */
    toggleEntry(key) {
      if (this.isExpanded(key)) {
        this.expandedEntryKeys = this.expandedEntryKeys.filter((entryKey) => entryKey !== key);
        return;
      }
      this.expandedEntryKeys = [...this.expandedEntryKeys, key];
    },

  },
};
